import React, { useEffect, useMemo } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import ButtonGradient from '../button-gradient';
import TextBase from '../text';
import { translate, MESSAGE_TYPE, messageManager } from '../../helper';
import { Colors } from '../../themes/Colors';
import { useAttachedPaymentMethod } from '../../hooks/usePayment';
import { resolveCssVarValue } from '../../utils/CommonFunction';

const StripeCardPaymentElement = ({
  onSuccess,
  goBack = () => { },
  submitLabel,
  disabled = false,
  mode = 'setup',
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { attachedPaymentMethod, isAddingPaymentMethod } = useAttachedPaymentMethod(() => {
    onSuccess?.();
    goBack();
  });

  const [fontSizeBase, setFontSizeBase] = React.useState(
    resolveCssVarValue('--fs-md2', { unit: 'px', fallback: '16px' })
  );

  useEffect(() => {
    const handleResize = () => {
      setFontSizeBase(
        resolveCssVarValue('--fs-md2', { unit: 'px', fallback: '16px' })
      );
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const appearance = useMemo(() => ({
    theme: 'stripe',
    variables: {
      fontFamily: 'Poppins, system-ui, sans-serif',
      fontSizeBase,
      colorPrimary: '#635bff',
      colorText: '#1a1a1a',
      colorTextSecondary: '#6b6b6b',
      borderRadius: '8px',
      spacingUnit: '8px',
    },
    rules: {
      '.Input': { border: '1px solid #e5e5e5', backgroundColor: '#f8f8f8' },
      '.Input:focus': { borderColor: '#cacaca' },
      '.Label': { fontWeight: '500' },
    },
  }), [fontSizeBase]);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    if (mode === 'payment') {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });
      if (error) {
        messageManager({ messageShow: error.message || translate('paymentError'), type: MESSAGE_TYPE.error });
        return;
      }
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        messageManager({ messageShow: translate('paymentSuccessful'), type: MESSAGE_TYPE.success });
        onSuccess?.(paymentIntent);
        goBack();
      }
    } else {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });
      if (error) {
        messageManager({ messageShow: error.message || translate('paymentError'), type: MESSAGE_TYPE.error });
        return;
      }
      if (setupIntent && setupIntent.payment_method) {
        await attachedPaymentMethod({ payment_id: setupIntent.payment_method });
        messageManager({ messageShow: translate('cardSuccessfullyAdded') || 'Card successfully added', type: MESSAGE_TYPE.success });
      }
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <PaymentElement id="payment-element" options={{ layout: 'tabs', defaultPaymentMethodType: 'card' }} appearance={appearance} />
      <ButtonGradient
        onClick={handleSubmit}
        loading={isAddingPaymentMethod}
        disabled={disabled || !stripe}
        textStyle={{ fontWeight: 700, fontSize: 'var(--fs-md2)' }}
        title={submitLabel || translate('proceed')}
        style={{ marginTop: 'calc(var(--sp-md2)*1.69811)', width: '100%' }}
      />
      {/* Optional error region could go here */}
      <TextBase size={'calc(var(--fs-xs)*1.32075)'} color={Colors.grey}>
        {translate('securePaymentPoweredByStripe')}
      </TextBase>
    </div>
  );
};

export default StripeCardPaymentElement;
